import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Alert, Badge, Button, Card, Empty, Segmented, Select, Space, Tag, Typography } from "antd";
import { BookOutlined, CalculatorOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { formatDay, formatEuro } from "../workReports/format";
import { getMeta, listCases } from "./api";
import { STATUS_COLORS, statusLabels } from "./labels";
import NewCaseModal from "./NewCaseModal";
import CalculatorModal from "./CalculatorModal";
import RulesModal from "./RulesModal";
import "./funding.css";

const today = () => dayjs().format("YYYY-MM-DD");

const Funding = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [meta, setMeta] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("open");
  const [program, setProgram] = useState(undefined);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newInitial, setNewInitial] = useState(null);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const labels = statusLabels(t);

  const loadMeta = () =>
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);

  useEffect(() => {
    loadMeta();
  }, []);

  // /user/funding?new=1&project=… (from the project page) opens the new-case dialog prefilled
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (meta && params.get("new")) {
      setNewInitial({
        project: params.get("project") || undefined,
        client: params.get("client") || undefined
      });
      setNewOpen(true);
      history.replace("/user/funding");
    }
  }, [meta, location.search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listCases({ searchText, status: status === "all" ? undefined : status, program })
      .then(res => !cancelled && setData(res.data.data))
      .catch(generalErrorAlert)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [searchText, status, program]);

  const columns = [
    {
      title: t("Funding case"),
      key: "number",
      mobile: "title",
      render: (_, item) => (
        <div>
          <Link to={`/user/funding/${item._id}`}>
            <Typography.Text strong>{item.number}</Typography.Text>
          </Link>
          <div className="fu-small fu-clamp">{item.programName}</div>
        </div>
      )
    },
    {
      title: t("Customer"),
      key: "client",
      mobile: "subtitle",
      render: (_, item) => (
        <div>
          <div>{[item.client?.name, item.project?.name].filter(Boolean).join(" · ") || "—"}</div>
          {item.address && <div className="fu-small fu-clamp">{item.address}</div>}
        </div>
      )
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      width: 170,
      render: (_, item) => (
        <Space size={[4, 4]} wrap>
          <Tag color={STATUS_COLORS[item.status]}>{labels[item.status]}</Tag>
          {item.errorCount > 0 && (
            <Tag color="error">{t("{{n}} problems", { n: item.errorCount })}</Tag>
          )}
        </Space>
      )
    },
    {
      title: t("Next deadline"),
      key: "deadline",
      width: 130,
      render: (_, item) =>
        item.nextDeadline ? (
          <Badge
            status={item.nextDeadline < today() ? "error" : "warning"}
            text={formatDay(item.nextDeadline)}
          />
        ) : (
          "—"
        )
    },
    {
      title: t("Estimate"),
      key: "estimate",
      width: 130,
      align: "right",
      render: (_, item) => formatEuro(item.estimate?.subsidyCents, lng)
    }
  ];

  const isEmpty = !loading && !data.length && !searchText && status === "open" && !program;

  return (
    <div className="funding-page">
      <PageToolbar
        filters={
          <Space direction="vertical" className="fu-full">
            <SearchBarComp handleSearch={setSearchText} />
            <Space wrap>
              <Segmented
                value={status}
                onChange={setStatus}
                options={[
                  { value: "open", label: t("Open") },
                  { value: "all", label: t("All") },
                  { value: "paid_out", label: labels.paid_out },
                  { value: "cancelled", label: labels.cancelled }
                ]}
              />
              <Select
                allowClear
                className="fu-program-filter"
                placeholder={t("All programmes")}
                value={program}
                onChange={setProgram}
                options={(meta?.programs || []).map(p => ({ value: p.key, label: p.name }))}
              />
            </Space>
          </Space>
        }
        actions={
          <Space wrap>
            <Button icon={<BookOutlined />} onClick={() => setRulesOpen(true)}>
              {t("Rules")}
            </Button>
            <Button icon={<CalculatorOutlined />} onClick={() => setCalculatorOpen(true)}>
              {t("Calculator")}
            </Button>
            {meta?.can.create && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setNewInitial(null);
                  setNewOpen(true);
                }}
              >
                {t("New funding case")}
              </Button>
            )}
          </Space>
        }
      />

      {meta?.rules && (
        <Alert
          className="mb-3"
          type={meta.rules.invalid ? "error" : "info"}
          showIcon
          message={t(
            "Funding rules as of {{version}}. Programmes change often – check against the official sources.",
            {
              version: meta.rules.version
            }
          )}
          action={
            <Button size="small" onClick={() => setRulesOpen(true)}>
              {t("Details")}
            </Button>
          }
        />
      )}

      {isEmpty ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>
                  {t("Funding paperwork for your customers")}
                </Typography.Title>
                <Typography.Paragraph type="secondary" className="fu-empty-text">
                  {t(
                    "Track BEG, KfW and § 35c cases from the first advice to the payout: the contract clause, the checklist, the deadlines, the contractor documents and an estimate of the subsidy."
                  )}
                </Typography.Paragraph>
              </>
            }
          >
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
                {t("New funding case")}
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        (() => {
          const table = (
            <ResponsiveTable
              rowKey="_id"
              loading={loading}
              columns={columns}
              dataSource={data}
              mobileAvatar={false}
              onRow={record => ({
                onDoubleClick: () => history.push(`/user/funding/${record._id}`)
              })}
              pagination={data.length > 50 ? { pageSize: 50 } : false}
              scroll={{ x: 760 }}
            />
          );
          return isMobile ? table : <Card styles={{ body: { padding: 0 } }}>{table}</Card>;
        })()
      )}

      <NewCaseModal
        open={newOpen}
        meta={meta}
        initial={newInitial}
        onClose={() => setNewOpen(false)}
      />
      <CalculatorModal open={calculatorOpen} meta={meta} onClose={() => setCalculatorOpen(false)} />
      <RulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} onChanged={loadMeta} />
    </div>
  );
};

export default Funding;
